// angular
import {
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { interval, Subscription } from 'rxjs';

export interface ITenderCountdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

@Component({
  selector: 'pwj-tender-countdown-timer',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './tender-countdown-timer.component.html',
  styleUrl: './tender-countdown-timer.component.scss',
})
export class TenderCountdownTimerComponent implements OnInit, OnDestroy {
  finishDate = input<string>();
  startDate = input<string>();

  countdown = signal<ITenderCountdown>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  isExpired = signal(false);

  estimatedLabel = computed(() => {
    const dateValue = this.finishDate() || this.startDate();
    if (!dateValue) {
      return '';
    }

    return new Date(dateValue)
      .toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      .toUpperCase();
  });

  private _subscription?: Subscription;
  private readonly _platformId = inject(PLATFORM_ID);

  ngOnInit(): void {
    this.updateCountdown();

    if (isPlatformBrowser(this._platformId)) {
      this._subscription = interval(1000).subscribe(() => {
        this.updateCountdown();
      });
    }
  }

  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
  }

  private updateCountdown(): void {
    const finishDate = this.finishDate();
    const startDate = this.startDate();
    const now = Date.now();

    if (startDate) {
      const startTime = new Date(startDate).getTime();
      if (startTime > now) {
        this.isExpired.set(false);
        this.countdown.set(this.calculateRemaining(startTime - now));
        return;
      }
    }

    if (!finishDate) {
      this.isExpired.set(true);
      return;
    }

    const finishTime = new Date(finishDate).getTime();
    const timeDifference = finishTime - now;

    if (timeDifference <= 0) {
      this.isExpired.set(true);
      this.countdown.set({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    this.isExpired.set(false);
    this.countdown.set(this.calculateRemaining(timeDifference));
  }

  private calculateRemaining(timeDifference: number): ITenderCountdown {
    const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor(
      (timeDifference % (1000 * 60 * 60)) / (1000 * 60)
    );
    const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  }
}
